import {memo, useMemo, useState} from 'react';

import styled from 'styled-components';
import {Form, Field} from 'react-final-form';
import {FORM_ERROR} from 'final-form';
import {useDispatch} from 'react-redux';

import {flipOnLTR, media} from '~/shared/theme/utils';
import {getLocalizationService} from '~/shared/services/localisationService';
import {FlexColumn, flexColumn} from '~/shared/theme/FlexLayout';
import {trackEvent} from '~/shared/services/analytics';
import {handleRefreshToken} from '~/shared/services/auth';
import {MoneycardOtlRuleManagement, OtlRuleDataLimitationName} from '~/shared/store/models/MoneycardOtlRuleManagement';
import {createLogger} from '~/shared/logging';
import {is401Error} from '~/shared/services/apiErrorService';
import {disableOtlRule, upsertOtlRule} from '~/shared/services/bffService/api';
import ToggleSwitch from '~/shared/components/ToggleSwitch';
import PieButton from '~/shared/theme/pie/PieButton';
import useOtlLimitationsInputs from '~/shared/components/Otl/useOtlLimitationsInputs';
import store from '~/shared/store';
import {setCurrentModal} from '~/shared/store/storeModules/currentModal/currentModalActions';
import failureImageSrc from '~/assets/images/failurePicture.png';
import OtlTermsOfUseCheckbox from '~/shared/components/Otl/OtlTermsOfUseCheckbox';

import OtlRuleLine from './OtlRuleLine';
import {Header, Explanation} from './components/styled';
import UnlinkIcon from './assets/unlink.svg?react';
import unlinkCardImgSrc from './assets/unlinkCardImg.png';
import unlinkCardSuccessSrc from './assets/unlinkCardSuccess.png';

const logger = createLogger('OtlRuleLimitations');

const Root = styled.div`
  flex: 1;
  ${flexColumn}
  ${flipOnLTR`
    text-align: right;
  `}
  ${media.minLargeMobile`
    flex: auto;
  `}
`;

const ToggleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Rules = styled.div`
  margin-top: 20px;
  border-radius: 12px;
  border: 1px solid ${({theme}) => theme.colors.borderStrong};
  padding: 0 16px;
`;

const Rule = styled.div<{withBorderTop?: boolean}>`
  padding: 16px 0;
  ${({withBorderTop}) => withBorderTop && 'border-top: 1px solid rgba(0, 0, 0, 0.08);'}
`;

const FieldError = styled.div`
  color: ${({theme}) => theme.checkout.errors.errorText};
`;

const SubmitButton = styled(PieButton).attrs({
  variant: 'primaryXL',
})`
  margin-top: 32px;
`;

const UnlinkCardButton = styled(PieButton).attrs({
  variant: 'ghostLarge',
})`
  margin-top: 16px;

  .button-content-container {
    gap: 4px;
  }
`;

const StyledToggleSwitch = styled(ToggleSwitch)`
  width: 48px;

  & > div {
    left: ${({isActive}) => (isActive ? '26px' : '2px')};
  }

  ${media.minLargeMobile`
    width: 48px;
    & > div {
      left: ${({isActive}) => (isActive ? '26px' : '2px')};
    }    
  `}
`;

const Container = styled(FlexColumn)`
  height: 100%;
  justify-content: space-between;
`;

const StyledOtlTermsOfUseCheckbox = styled(OtlTermsOfUseCheckbox)`
  margin-top: 24px;
`;

const RuleNames = {
  SingleTransaction: 'max_per_transaction',
  Daily: 'max_per_day',
  Monthly: 'max_per_month',
};

type OtlRuleLimitationsProps = {
  moneycardId: string;
  shouldShowTermsOfUse: boolean;
  otlRule?: MoneycardOtlRuleManagement['otlRule'];
  selectedCreditCard: number | null;
  onSuccess?: () => void;
  showUnlinkButton?: boolean;
  onOtlFailure?: () => void;
  onUnlinkSuccess?: (() => void) | (() => Promise<void>);
};

const OtlRuleLimitations = ({
  moneycardId,
  shouldShowTermsOfUse,
  otlRule,
  selectedCreditCard,
  onSuccess,
  showUnlinkButton,
  onOtlFailure,
  onUnlinkSuccess,
}: OtlRuleLimitationsProps) => {
  const {t} = getLocalizationService();
  const dispatch = useDispatch();
  const [isTermsOfUseConfirmed, setIsTermsOfUseConfirmed] = useState(false);

  const {limitations} = otlRule || {};

  const rules = useMemo(
    () =>
      limitations?.map(({limitationType}) => ({
        limitationType,
        ruleName: t(RuleNames[limitationType]),
        inputType: 'number',
        parse: ({value}: {value: string}) => ({
          value: Math.max(parseInt(value, 10) || 0, 0),
        }),
      })) || [],
    [limitations, t],
  );

  const initialValues = useMemo(
    () =>
      limitations?.reduce<{
        [key in Partial<OtlRuleDataLimitationName>]?: {value: string}
      }>(
        (acc, {limitationType, value, isActive}) => ({
          ...acc,
          [limitationType]: {value: isActive ? value : ''},
        }),
        {},
      ) || {},
    [limitations],
  );

  const {
    singleTransactionValue,
    dailyValue,
    monthlyValue,
    inputValidators,
    formValidator,
    onChange,
  } = useOtlLimitationsInputs({
    SingleTransaction: Number(initialValues[OtlRuleDataLimitationName.SINGLE_TRANSACTION]?.value),
    Daily: Number(initialValues[OtlRuleDataLimitationName.DAILY]?.value),
    Monthly: Number(initialValues[OtlRuleDataLimitationName.MONTHLY]?.value),
  });

  const [areLimitationsEnabled, setAreLimitationsEnabled] = useState(limitations?.some(l => l.isActive) || false);

  const onToggleSwitch = (newState: boolean) => {
    trackEvent('otlLimitations_toggle', {
      toggleOption: newState,
    });
    setAreLimitationsEnabled(newState);
  };

  const onUnlinkConfirmClick = async () => {
    if (!otlRule?.ruleId) {
      logger.warn('No ruleId to unlink credit card');
      return;
    }
    try {
      await disableOtlRule(otlRule?.ruleId);
      trackEvent('linkCC2MC_remove');
      if (onUnlinkSuccess) {
        onUnlinkSuccess();
      }
      dispatch(setCurrentModal('moneycardFeedbackModal', {
        imgSrc: unlinkCardSuccessSrc,
        imgAlt: t('success'),
        header: t('credit_card_successfully_removed_from_10bis'),
        primaryButton: {
          label: t('close_2'),
          onClick: () => dispatch(setCurrentModal(null)),
        },
      }));
    } catch (e) {
      if (is401Error(e)) {
        await handleRefreshToken(e, onUnlinkConfirmClick);
        return;
      }
      logger.warn('Unexpected error', e);
      dispatch(setCurrentModal('moneycardFeedbackModal', {
        imgSrc: failureImageSrc,
        imgAlt: t('failure_picture'),
        header: t('we_couldnt_remove_the_card'),
        subHeader: t('try_again_later_2'),
        primaryButton: {
          label: t('close_2'),
          onClick: () => dispatch(setCurrentModal(null)),
        },
      }));
    }
  };

  const onUnlinkClick = () => {
    dispatch(setCurrentModal('moneycardFeedbackModal', {
      imgSrc: unlinkCardImgSrc,
      imgAlt: t('failure_picture'),
      header: t('unlink_credit_card_question'),
      subHeader: t('you_will_be_able_to_pay_with_your_balance_only'),
      primaryButton: {
        label: t('yes_remove_the_card'),
        onClick: onUnlinkConfirmClick,
      },
      secondaryButton: {
        label: t('cancel_card_decline'),
        onClick: () => store.dispatch(setCurrentModal(null)),
      },
    }));
  };

  const onSubmit = async () => {
    trackEvent('hasApprovedCreditCard');

    const handleSubmit = async () => {
      if (!otlRule || !selectedCreditCard) {
        logger.error('No otlRuleData or credit card was provided');
        return;
      }

      const formLimitations = (() => {
        const data = {
          singleTransactionLimit: 0,
          singleTransactionLimitEnabled: false,
          dailyLimit: 0,
          dailyLimitEnabled: false,
          monthlyLimit: 0,
          monthlyLimitEnabled: false,
        };

        if (areLimitationsEnabled) {
          if (singleTransactionValue) {
            data.singleTransactionLimit = singleTransactionValue;
            data.singleTransactionLimitEnabled = true;
          }

          if (dailyValue) {
            data.dailyLimit = dailyValue;
            data.dailyLimitEnabled = true;
          }

          if (monthlyValue) {
            data.monthlyLimit = monthlyValue;
            data.monthlyLimitEnabled = true;
          }
        }

        return data;
      })();

      trackEvent('otlLimitations_update', {
        ccTransactionLimit: formLimitations.singleTransactionLimit,
        ccDailyLimit: formLimitations.dailyLimit,
        ccMonthlyLimit: formLimitations.monthlyLimit,
      });

      await upsertOtlRule({
        ...otlRule,
        encryptedMoneycardId: moneycardId,
        ruleActive: true,
        creditcardId: selectedCreditCard,
        ...formLimitations,
      });
      // if there was no error, initiating onSuccess callback
      if (onSuccess) {
        trackEvent('creditcardLimitationAdded', formLimitations);
        onSuccess();
      }
    };

    try {
      await handleSubmit();
    } catch (error) {
      if (is401Error(error)) {
        await handleRefreshToken(error, handleSubmit);
        return;
      }
      onOtlFailure?.();
      return {[FORM_ERROR]: error instanceof Error ? error.message : 'Unexpected error occurred'};
    }
  };

  return (
    <Root>
      <Form
        initialValues={initialValues}
        onSubmit={onSubmit}
        validate={formValidator}
        render={({handleSubmit, errors, submitFailed}) => (
          <Container>
            <ToggleContainer>
              <FlexColumn>
                <Header>{t('credit_charge_limit')}</Header>
                <Explanation>{t('want_to_limit_the_charge_amount')}</Explanation>
              </FlexColumn>
              <StyledToggleSwitch isActive={areLimitationsEnabled} onClick={onToggleSwitch} />
            </ToggleContainer>
            {areLimitationsEnabled && (
              <>
                <Rules>
                  {rules.map(({limitationType, parse, ...rest}, index) => (
                    <Rule key={limitationType} withBorderTop={!!index}>
                      <Field
                        name={limitationType}
                        parse={parse}
                        validate={inputValidators[limitationType]}
                        render={({input, meta}) => (
                          <OtlRuleLine {...rest} {...input} onLimitChange={onChange} meta={meta} />
                        )}
                      />
                    </Rule>
                  ))}
                </Rules>
              </>
            )}
            <FlexColumn>
              {shouldShowTermsOfUse && (
                <StyledOtlTermsOfUseCheckbox initialValue={isTermsOfUseConfirmed} onChange={setIsTermsOfUseConfirmed} />
              )}
              <FieldError>{submitFailed && errors && errors[FORM_ERROR] && t(errors[FORM_ERROR])}</FieldError>
              <SubmitButton
                onClick={handleSubmit}
                disabled={((!!errors && errors[FORM_ERROR]) || (shouldShowTermsOfUse && !isTermsOfUseConfirmed))}
              >
                {t('save_changes')}
              </SubmitButton>
              {showUnlinkButton && (
                <UnlinkCardButton onClick={onUnlinkClick}>
                  <UnlinkIcon />
                  {t('unlink_credit_card')}
                </UnlinkCardButton>
              )}
            </FlexColumn>
          </Container>
        )}
      />
    </Root>
  );
};

export default memo(OtlRuleLimitations);
